import React, { useRef } from 'react';
import { Box, Flex } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { SearchTagsDropdown } from 'components/molecules/searchTagsDropdown';
import { useAddTagToSingleContacts } from 'components/organisms/contactActionPanel/hooks/useAddTagToSingleContacts';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';

export const ContactTagsRenderer = ({ data, value }) => {
  const isPublicView = data?.options?.type === 'public';
  const { onChangeTag, onCreateTag, isLoading } = useAddTagToSingleContacts({
    queryParams: data.options.queryParams,
    contact: data.original,
    activeNetwork: data.options.activeNetwork,
  });

  const { colors } = useTheme();

  const ref = useRef(null);

  if (data.options.showLoader) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={120}
        viewBox="0 0 400 120"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
      >
        <rect x="20" y="16" rx="3" ry="3" width="80" height="8" />
      </ContentLoader>
    );
  }

  if (!value || value?.length === 0) {
    return '';
  }

  return (
    <Box
      sx={{
        height: '100%',
        position: 'relative',
        width: '100%',
        justifyContent: 'space-between',
        ...(!isPublicView && {
          '&:hover': {
            '#contact-tags-dropdown': {
              display: 'flex',
            },
          },
        }),
      }}
    >
      <Flex
        p="8px"
        alignItems="center"
        sx={{
          width: 'max-content',
          height: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Flex ref={ref} sx={{ gap: '4px', alignItems: 'center', width: 'max-content' }}>
          {value.map((tag) => (
            <Box
              maxHeight="24px"
              key={tag.id}
              width="max-content"
              sx={{
                color: 'text.main',
                backgroundColor: 'neutral.0',
                borderRadius: '99px',
                padding: '4px 12px',
                border: '1px solid',
                borderColor: 'border.subtle',
                fontSize: '14px',
                lineHeight: '16px',
              }}
            >
              {tag.tag}
            </Box>
          ))}
        </Flex>

        {!isPublicView && (
          <SearchTagsDropdown
            placement="bottom-end"
            trigger={
              <Box
                id="contact-tags-dropdown"
                sx={{
                  display: 'none',
                  bg: 'neutral.20',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'absolute',
                  top: '0px',
                  right: '0px',
                  bottom: '0px',
                  padding: '8px',
                }}
              >
                <Box
                  as={ChevronDown}
                  sx={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    color: 'text.main',
                  }}
                />
              </Box>
            }
            value={data.original.tags.map((item) => ({ id: item.id, label: item.tag, value: item.id }))}
            onChange={onChangeTag}
            onCreate={onCreateTag}
            isLoading={isLoading}
          />
        )}
      </Flex>
    </Box>
  );
};

ContactTagsRenderer.propTypes = {
  data: PropTypes.object.isRequired,
  value: PropTypes.array,
};

ContactTagsRenderer.defaultProps = {
  value: [],
};
